/**
 * Unified application configuration
 * All values sourced from environment variables (import.meta.env)
 */

// Cache-bust token appended to the widget URLs so browsers pick up new widget
// builds instead of a stale cached copy. Bump on widget releases; with
// Cache-Control on the widget, future releases propagate on their own.
const WIDGET_CACHE_BUST = "1.8.0";

const withCacheBust = (url) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${WIDGET_CACHE_BUST}` : url;

const config = {
  // Environment
  env: import.meta.env.MODE,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,

  // Parse Backend
  parse: {
    appId: import.meta.env.VITE_PARSE_APP_ID,
    jsKey: import.meta.env.VITE_PARSE_JS_KEY,
    serverUrl: import.meta.env.VITE_PARSE_SERVER_URL,
    failoverUrl: import.meta.env.VITE_PARSE_FAILOVER_URL || null,
  },

  // Stripe Payments
  stripe: {
    publicKey: import.meta.env.VITE_STRIPE_PUBLIC_KEY,
    endpointBase: import.meta.env.VITE_STRIPE_ENDPOINT_BASE,
    monthlyPlanId: import.meta.env.VITE_STRIPE_MONTHLY_PLAN_ID,
    yearlyPlanId: import.meta.env.VITE_STRIPE_YEARLY_PLAN_ID,
    // Derived endpoints
    get createCheckout() {
      return `${this.endpointBase}/createCheckout`;
    },
    get plans() {
      return `${this.endpointBase}/plans`;
    },
    get customers() {
      return `${this.endpointBase}/customers`;
    },
    get subscriptions() {
      return `${this.endpointBase}/subscriptions`;
    },
    get coupons() {
      return `${this.endpointBase}/coupons`;
    },
  },

  // Facebook
  facebook: {
    appId: import.meta.env.VITE_FB_APP_ID,
  },

  // SkySafari Widget
  widget: {
    host: import.meta.env.VITE_WIDGET_HOST,
    scriptUrl: withCacheBust(import.meta.env.VITE_WIDGET_SCRIPT_URL),
    cssUrl: withCacheBust(import.meta.env.VITE_WIDGET_CSS_URL),
  },

  // Analytics
  analytics: {
    gaTrackingId: import.meta.env.VITE_GA_TRACKING_ID,
  },

  // SkySafari (shared key)
  skysafari: {
    key:
      import.meta.env.VITE_SKYSAFARI_API_KEY ||
      import.meta.env.VITE_SKYSAFARI_KEY,
  },

  // Cloudflare Turnstile
  turnstile: {
    siteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
  },
};

// Validate required config on load (dev only)
if (import.meta.env.DEV) {
  const required = [
    ["VITE_PARSE_APP_ID", config.parse.appId],
    ["VITE_PARSE_JS_KEY", config.parse.jsKey],
    ["VITE_PARSE_SERVER_URL", config.parse.serverUrl],
  ];

  const missing = required.filter(([, value]) => !value);
  if (missing.length > 0) {
    console.warn(
      "Missing required env vars:",
      missing.map(([name]) => name).join(", "),
    );
  }
}

export default config;
